import { Position } from "@xyflow/react";
import { StickyNote, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import type { ActionNodeReturnOrderNoteConfigDto } from "@babackapp/private-api-client/types";
import { BaseHandle } from "../../components/base-handle";
import { BaseNode, BaseNodeContent } from "../../components/base-node";
import { useNodeData } from "../../hooks/use-node-data";

export const RETURN_ORDER_NOTE_NODE_WIDTH = 280;
export const RETURN_ORDER_NOTE_NODE_HEIGHT_ESTIMATION = 120;

type LiveData = {
  actionType: string;
  config: ActionNodeReturnOrderNoteConfigDto;
};

export function ReturnOrderNoteActionNodeEditor({ id }: { id: string }) {
  const { t } = useTranslation("trackoffice");
  const liveData = useNodeData<LiveData>(id);
  const note =
    liveData?.actionType === "set_order_note" ? liveData.config?.note : "";

  return (
    <ContextualTipsTrigger
      tips={[
        {
          id: "workflow-node-return-order-note",
          Icon: StickyNote,
          title: t(
            "workflowsEditor.tips.workflowNodeOrderNote.title",
            "This is an order note action node",
          ),
          description: t(
            "workflowsEditor.tips.workflowNodeOrderNote.desc",
            "This node replaces the Shopify order note when the workflow runs.",
          ),
        },
      ]}
    >
      <BaseNode nodeId={id} style={{ position: "relative" }}>
        <BaseNodeContent>
          <div className="flex flex-col gap-1 w-full">
            <span className="text-xs text-muted-foreground">
              {t("workflowsEditor.returnOrderNote.title", "Order note")}
            </span>
            {note ? (
              <span className="text-xs mt-1 font-semibold line-clamp-3 break-words">
                {note}
              </span>
            ) : (
              <span className="text-xs mt-1 font-semibold text-amber-600 flex items-center gap-1">
                <TriangleAlert className="size-3 shrink-0" />
                {t(
                  "workflowsEditor.returnOrderNote.missing",
                  "Write the note to apply",
                )}
              </span>
            )}
          </div>
          <BaseHandle type="target" position={Position.Left} />
          <BaseHandle
            type="source"
            position={Position.Right}
            style={{ top: "50%", transform: "translateY(-50%)" }}
          />
        </BaseNodeContent>
      </BaseNode>
    </ContextualTipsTrigger>
  );
}
